import React from 'react';
import { useUser } from '../contexts/UserContext';
import { SubscriptionStatus } from '../components/SubscriptionStatus';
import { isFreeBeta } from '../utils/quizAccess';
import { PasswordChangeForm } from '../components/PasswordChangeForm';
import { EmailPreferencesForm } from '../components/EmailPreferencesForm';
import { Section, Grid } from '../components/BaseComponents';

/**
 * AccountPage - User account management page
 *
 * Provides a centralized location for users to manage their account settings:
 * - Subscription management (reuses existing SubscriptionStatus component)
 * - Password change functionality
 * - Email communication preferences
 *
 * Uses a responsive grid layout that adapts to different screen sizes
 */
export const AccountPage: React.FC = () => {
    const { currentUser, isLoading } = useUser();

    // Redirect unauthenticated users
    if (!currentUser && !isLoading) {
        return (
            <div className="max-w-4xl mx-auto px-4">
                <Section title="Account Management">
                    <div className="text-center py-8">
                        <p className="text-neutral-600">Please log in to access your account settings.</p>
                    </div>
                </Section>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto px-4">
                <Section title="Account Management">
                    <div className="text-center py-8">
                        <p className="text-neutral-600">Loading...</p>
                    </div>
                </Section>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4">
            <Section
                title="Account Management"
                subtitle={`Manage your account settings, ${currentUser?.firstName}`}
                className="mb-8"
            >
                <Grid cols={1} gap="lg" className="max-w-none">
                    {/* Subscription Management Section (hidden in the free beta) */}
                    {!isFreeBeta() && (
                    <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-4">Subscription</h3>
                        <SubscriptionStatus />
                    </div>
                    )}

                    {/* Password Change Section */}
                    <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-4">Password</h3>
                        <PasswordChangeForm />
                    </div>

                    {/* Email Preferences Section */}
                    <div>
                        <h3 className="text-lg font-medium text-neutral-900 mb-4">Email Preferences</h3>
                        <EmailPreferencesForm />
                    </div>
                </Grid>
            </Section>
        </div>
    );
};

export default AccountPage;